import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  Textarea,
  Select,
  Checkbox,
  IconButton,
  useToast,
  useColorModeValue as mode,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
  Badge,
  Spinner,
  Stack
} from "@chakra-ui/react"
import { Tooltip } from "@chakra-ui/react"
import { AddIcon, EditIcon, DeleteIcon, InfoOutlineIcon } from "@chakra-ui/icons"
import { getOrders, createOrder, updateOrder, deleteOrder } from "../redux/actions/orderActions"

const CATEGORIES = ["Stock", "Material", "Tools & Equipment", "Other"]
const SUPPLIERS = ["Ali Express", "Mobile Sentrix", "DigiKey", "eBay", "Amazon", "Other"]
const PRIORITIES = ["Low", "Normal", "High"]

const OrdersScreen = () => {
  const dispatch = useDispatch()
  const toast = useToast()
  const { orders, loading, error } = useSelector(state => state.order)
  const { isOpen, onOpen, onClose } = useDisclosure()
  
  const [isEditMode, setIsEditMode] = useState(false)
  const [currentOrderId, setCurrentOrderId] = useState(null)
  const [filterCategory, setFilterCategory] = useState("All")
  const [filterStatus, setFilterStatus] = useState("All")
  const [filterPriority, setFilterPriority] = useState("All")
  const nowLocal = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)

  const [formData, setFormData] = useState({
    dateTime: nowLocal(),
    itemName: "",
    category: "Stock",
    supplier: "Ali Express",
    quantity: "",
    priority: "Normal",
    comments: "",
    done: false
  })

  useEffect(() => {
    dispatch(getOrders())
  }, [dispatch])

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error,
        status: "error",
        duration: 3000,
        isClosable: true
      })
    }
  }, [error, toast])

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }))
  }

  const resetForm = () => {
    setFormData({
      dateTime: nowLocal(),
      itemName: "",
      category: "Stock",
      supplier: "Ali Express",
      quantity: "",
      priority: "Normal",
      comments: "",
      done: false
    })
    setIsEditMode(false)
    setCurrentOrderId(null)
  }

  const handleOpenAdd = () => {
    resetForm()
    onOpen()
  }

  const handleOpenEdit = order => {
    setIsEditMode(true)
    setCurrentOrderId(order._id)
    setFormData({
      dateTime: order.dateTime ? new Date(new Date(order.dateTime).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : nowLocal(),
      itemName: order.itemName || "",
      category: order.category || "Stock",
      supplier: order.supplier || "Ali Express",
      quantity: order.quantity || "",
      priority: order.priority || "Normal",
      comments: order.comments || "",
      done: order.done || false
    })
    onOpen()
  }

  const handleSubmit = async () => {
    if (!formData.itemName || !formData.quantity || !formData.dateTime) {
      toast({
        title: "Validation Error",
        description: "Please fill in Item Name and Quantity",
        status: "warning",
        duration: 3000,
        isClosable: true
      })
      return
    }

    try {
      if (isEditMode) {
        await dispatch(updateOrder(currentOrderId, formData))
        toast({
          title: "Success",
          description: "Order updated successfully",
          status: "success",
          duration: 2000,
          isClosable: true
        })
      } else {
        await dispatch(createOrder(formData))
        toast({
          title: "Success",
          description: "Order created successfully",
          status: "success",
          duration: 2000,
          isClosable: true
        })
      }
      onClose()
      resetForm()
    } catch (err) {
      // Error already handled by redux action
    }
  }

  const handleDelete = async id => {
    if (window.confirm("Are you sure you want to delete this order?")) {
      try {
        await dispatch(deleteOrder(id))
        toast({
          title: "Success",
          description: "Order deleted successfully",
          status: "success",
          duration: 2000,
          isClosable: true
        })
      } catch (err) {
        // Error already handled by redux action
      }
    }
  }

  const formatDateTime = dateStr => {
    if (!dateStr) return ""
    const date = new Date(dateStr)
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    })
  }

  const getPriorityColor = priority => {
    switch (priority) {
      case "High": return "red"
      case "Normal": return "blue"
      case "Low": return "gray"
      default: return "blue"
    }
  }

  const bgColor = mode("white", "gray.800")
  const borderColor = mode("gray.200", "gray.600")
  const hoverBg = mode("gray.50", "gray.700")
  const textColor = mode("gray.500", "gray.400")
  
  const filteredOrders = orders.filter(o => {
    const catOk = filterCategory === "All" ? true : o.category === filterCategory
    const statusOk = filterStatus === "All" ? true : filterStatus === "Done" ? o.done === true : o.done === false
    const priorityOk = filterPriority === "All" ? true : o.priority === filterPriority
    return catOk && statusOk && priorityOk
  })

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    if (a.done === b.done) return 0
    return a.done ? 1 : -1
  })

  return (
    <Box maxW="1200px" mx="auto" p={6}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="lg">Orders Manager</Heading>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={handleOpenAdd}>
          Add Order
        </Button>
      </Flex>

      <HStack spacing={3} mb={6} flexWrap="wrap">
        <FormControl maxW="220px">
          <FormLabel mb={1}>Category</FormLabel>
          <Select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <option value="All">All</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl maxW="220px">
          <FormLabel mb={1}>Status</FormLabel>
          <Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="All">All</option>
            <option value="Pending">Pending</option>
            <option value="Done">Done</option>
          </Select>
        </FormControl>
        <FormControl maxW="220px">
          <FormLabel mb={1}>Priority</FormLabel>
          <Select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
            <option value="All">All</option>
            {PRIORITIES.map(pri => (
              <option key={pri} value={pri}>{pri}</option>
            ))}
          </Select>
        </FormControl>
      </HStack>

      {loading && orders.length === 0 ? (
        <Flex justify="center" py={10}>
          <Spinner size="xl" />
        </Flex>
      ) : orders.length === 0 ? (
        <Box textAlign="center" py={10}>
          <Text fontSize="lg" color="gray.500">
            No orders found. Click "Add Order" to create one.
          </Text>
        </Box>
      ) : (
        <VStack spacing={3} align="stretch">
          {sortedOrders.map(order => (
            <Box
              key={order._id}
              p={4}
              bg={bgColor}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="md"
              _hover={{ bg: hoverBg }}
              transition="all 0.2s"
            >
              <Flex justify="space-between" align="start">
                <Box flex="1">
                  <HStack mb={2} spacing={3}>
                    <Badge colorScheme={order.done ? "green" : "orange"}>
                      {order.done ? "Done" : "Pending"}
                    </Badge>
                    <Badge colorScheme="purple">{order.category}</Badge>
                    <Badge colorScheme={getPriorityColor(order.priority)}>{order.priority}</Badge>
                    <Text fontSize="sm" color="gray.500">
                      {formatDateTime(order.dateTime)}
                    </Text>
                  </HStack>
                  <HStack mb={2} spacing={2} align="center">
                    {order.comments && (
                      <Tooltip label={order.comments} hasArrow placement="top-start">
                        <InfoOutlineIcon color="gray.500" />
                      </Tooltip>
                    )}
                    <Text fontWeight="bold" fontSize="lg" color={order.done ? textColor : "inherit"}>
                      {order.itemName}
                    </Text>
                  </HStack>
                  <Stack direction={{ base: "column", md: "row" }} spacing={4} fontSize="sm">
                    <HStack>
                      <Text fontWeight="semibold">Supplier:</Text>
                      <Text>{order.supplier}</Text>
                    </HStack>
                    <HStack>
                      <Text fontWeight="semibold">Quantity:</Text>
                      <Text>{order.quantity}</Text>
                    </HStack>
                  </Stack>
                </Box>
                <HStack spacing={2}>
                  <IconButton
                    icon={<EditIcon />}
                    size="sm"
                    colorScheme="yellow"
                    aria-label="Edit order"
                    onClick={() => handleOpenEdit(order)}
                  />
                  <IconButton
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    aria-label="Delete order"
                    onClick={() => handleDelete(order._id)}
                  />
                </HStack>
              </Flex>
            </Box>
          ))}
        </VStack>
      )}

      {/* Add/Edit Order Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isEditMode ? "Edit Order" : "Add New Order"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Date & Time</FormLabel>
                <Input
                  type="datetime-local"
                  name="dateTime"
                  value={formData.dateTime}
                  isReadOnly
                  color={textColor}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Item Name</FormLabel>
                <Input
                  name="itemName"
                  value={formData.itemName}
                  onChange={handleInputChange}
                  placeholder="Enter item name"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select name="category" value={formData.category} onChange={handleInputChange}>
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Supplier</FormLabel>
                <Select name="supplier" value={formData.supplier} onChange={handleInputChange}>
                  {SUPPLIERS.map(sup => (
                    <option key={sup} value={sup}>
                      {sup}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Quantity to Order</FormLabel>
                <Input
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  placeholder="Enter quantity"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Priority</FormLabel>
                <Select name="priority" value={formData.priority} onChange={handleInputChange}>
                  {PRIORITIES.map(pri => (
                    <option key={pri} value={pri}>
                      {pri}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Comments (optional)</FormLabel>
                <Textarea
                  name="comments"
                  value={formData.comments}
                  onChange={handleInputChange}
                  placeholder="Enter comments"
                  rows={4}
                />
              </FormControl>

              <FormControl>
                <Checkbox name="done" isChecked={formData.done} onChange={handleInputChange}>
                  Mark as Done
                </Checkbox>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              {isEditMode ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default OrdersScreen
